import { FC, ReactNode, useEffect, useState } from 'react';

import { usePage } from '@inertiajs/react';
import { Filter } from 'lucide-react';

import { ConfiguredPropertyFilter } from './ConfiguredPropertyFilter';
import { expansionFilterOptions } from './expansionFilterOptions';
import { PropertyFilterSelect } from './PropertyFilterSelect';
import { Button } from '../../../components/ui/Button';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from '../../../components/ui/Sheet';
import { Expansion, ProductFilter, ProductType } from '../../../types';
import { PropertyOption } from '../../../types/shared';

type PageProps = {
    currentProductType: ProductType | null;
    filtersConfig: Record<string, ProductFilter[]>;
    expansions: Expansion[] | null;
};

interface Props {
    readonly filters: Record<string, string>;
    readonly onFilterChange: (filters: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => void;
    readonly expansion: Expansion | null;
    readonly extraFilters?: {
        key: string;
        label: string;
        allLabel: string;
        options: PropertyOption[];
        searchable?: boolean;
        multiselect?: boolean;
        testId?: string;
    }[];
    readonly fallbackProductTypeSlug?: string;
    readonly hidePropertyFiltersWithoutProductType?: boolean;
    readonly topContent?: ReactNode;
    readonly variant?: 'default' | 'seller';
}

export const MobileFilters: FC<Props> = ({
    filters,
    onFilterChange,
    expansion,
    extraFilters,
    fallbackProductTypeSlug,
    hidePropertyFiltersWithoutProductType = false,
    topContent,
}) => {
    const { currentProductType, filtersConfig, expansions } =
        usePage<PageProps>().props;
    const expansionOptions = expansionFilterOptions(expansions);

    const [open, setOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState<{
        q: Record<string, string>;
        expansionSlug: string | undefined;
    }>({
        q: filters,
        expansionSlug: expansion?.slug,
    });

    const resolvedFallbackProductTypeSlug =
        (fallbackProductTypeSlug && filtersConfig[fallbackProductTypeSlug]
            ? fallbackProductTypeSlug
            : undefined) ?? Object.keys(filtersConfig)[0];
    const currentFiltersConfig =
        (hidePropertyFiltersWithoutProductType && !currentProductType
            ? []
            : currentProductType
              ? filtersConfig[currentProductType.slug]
              : filtersConfig[resolvedFallbackProductTypeSlug]) ?? [];
    const filterTriggerClassName = 'rounded-xl';
    const filterContentClassName = 'rounded-2xl';
    const actionButtonClassName = 'w-full rounded-xl';
    const triggerButtonClassName =
        'fixed right-4 bottom-20 z-50 h-12 rounded-xl border border-border/70 bg-background/95 px-4 text-foreground shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/85';

    useEffect(() => {
        setLocalFilters({
            q: filters,
            expansionSlug: expansion?.slug,
        });
    }, [expansion?.slug, filters]);

    const handleExpansionChange = (expansionSlug: string) => {
        setLocalFilters((current) => ({
            q: current.q,
            expansionSlug: expansionSlug,
        }));
    };

    const handlePropertyChange = (updates: Record<string, string>) => {
        setLocalFilters((current) => ({
            q: {
                ...current.q,
                ...updates,
            },
            expansionSlug: current.expansionSlug ?? expansion?.slug,
        }));
    };

    const handleChange = (propertyId: string, optionId: string) => {
        handlePropertyChange({ [propertyId]: optionId });
    };

    const handleApplyFilters = () => {
        setOpen(false);
        onFilterChange(localFilters);
    };

    return (
        <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="default"
                        size="default"
                        className={triggerButtonClassName}
                        data-testid="mobile-filters-trigger"
                    >
                        <Filter className="h-5 w-5" />
                        <span className="ml-2 text-sm font-medium">
                            Фільтри
                        </span>
                    </Button>
                </SheetTrigger>
                <SheetContent
                    title="Фільтри"
                    side="bottom"
                    className="h-[100dvh] overflow-hidden rounded-none p-0"
                >
                    <div className="flex h-full flex-col">
                        <div className="px-4 pt-4">
                            <h3 className="mb-4 text-center text-lg font-medium">
                                Фільтри
                            </h3>
                        </div>
                        <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
                            {topContent && (
                                <div className="-mx-1 overflow-x-auto overflow-y-hidden pb-1">
                                    <div className="px-1">{topContent}</div>
                                </div>
                            )}

                            {extraFilters?.map((filter) => (
                                <div key={filter.key}>
                                    <label className="mb-2 block text-sm font-medium">
                                        {filter.label}
                                    </label>
                                    <PropertyFilterSelect
                                        value={
                                            localFilters.q[filter.key] || 'all'
                                        }
                                        onChange={(val) =>
                                            handleChange(filter.key, val)
                                        }
                                        options={filter.options}
                                        searchable={filter.searchable}
                                        multiselect={filter.multiselect}
                                        allLabel={filter.allLabel}
                                        testId={filter.testId}
                                        triggerClassName={
                                            filterTriggerClassName
                                        }
                                        contentClassName={
                                            filterContentClassName
                                        }
                                    />
                                </div>
                            ))}

                            {!(
                                expansionOptions.length === 0 ||
                                currentProductType?.slug === 'accessories' ||
                                currentProductType?.slug === 'sealed'
                            ) && (
                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Колекції
                                    </label>
                                    <PropertyFilterSelect
                                        value={
                                            localFilters.expansionSlug || 'all'
                                        }
                                        onChange={(val) =>
                                            handleExpansionChange(val)
                                        }
                                        options={expansionOptions}
                                        searchable
                                        showSelectionIndicator={false}
                                        allLabel="Усі колекції"
                                        searchPlaceholder="Пошук колекцій..."
                                        emptyText="Колекції не знайдено"
                                        testId="expansion-filter"
                                        triggerClassName={
                                            filterTriggerClassName
                                        }
                                        contentClassName={
                                            filterContentClassName
                                        }
                                    />
                                </div>
                            )}
                            {currentFiltersConfig?.map((product_property) => (
                                <div key={product_property.id}>
                                    <label className="mb-2 block text-sm font-medium">
                                        {product_property.name}
                                    </label>
                                    <ConfiguredPropertyFilter
                                        property={product_property}
                                        values={localFilters.q}
                                        onChange={handlePropertyChange}
                                        triggerClassName={
                                            filterTriggerClassName
                                        }
                                        contentClassName={
                                            filterContentClassName
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="space-y-3 border-t px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                            <Button
                                className={actionButtonClassName}
                                onClick={handleApplyFilters}
                            >
                                Застосувати фільтри
                            </Button>
                            <Button
                                variant="outline"
                                className={actionButtonClassName}
                                onClick={() => setOpen(false)}
                            >
                                Сховати фільтри
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};
